// AutocompleteCities.tsx
import { ApiRequest } from 'GlobalFunctions';
import { useEffect, useState, useCallback, useRef } from 'react';
import { debounce } from 'lodash';

export default function AutocompleteCities(props: any) {
    const { formValues, setFormValues, pc_city, pc_state, attributeName = 'pc' } = props;
    const [cities, setCities] = useState([]);
    const [inputPosition, setInputPosition] = useState({ top: 0, left: 0, width: 0 });
    const [isVisible, setIsVisible] = useState(false);
    const [previousValue, setPreviousValue] = useState('');
    const [isSelecting, setIsSelecting] = useState(false);

    // Referencias para acceder a valores actuales en el debounce y event listeners
    const formValuesRef = useRef(formValues);
    const pcStateRef = useRef(pc_state);
    const citiesRef = useRef(cities);
    const isSelectingRef = useRef(isSelecting);
    const previousValueRef = useRef(previousValue);

    // Actualizar las referencias cuando cambien los valores
    useEffect(() => {
        formValuesRef.current = formValues;
    }, [formValues]);

    useEffect(() => {
        pcStateRef.current = pc_state;
    }, [pc_state]);

    // Actualizar las referencias cuando cambien los estados
    useEffect(() => {
        citiesRef.current = cities;
    }, [cities]);

    useEffect(() => {
        isSelectingRef.current = isSelecting;
    }, [isSelecting]);

    useEffect(() => {
        previousValueRef.current = previousValue;
    }, [previousValue]);

    /*=========================================================
    GET CITIES
    =========================================================*/
    const getCities = async (keyword: any) => {
        console.log('getCities called with keyword:', keyword); // Debug log
        let region_id;
        if (pcStateRef.current === 's-state') {
            region_id = formValuesRef.current.s_state;
        } else {
            region_id = formValuesRef.current.state;
        }
        if (!region_id) {
            setCities([]);
            return;
        }
        await ApiRequest({
            url: '/countries/getCities',
            method: 'GET',
            query: {
                region_id,
                keyword
            },
            setResponse: (response: any) => {
                setCities(response);
            }
        });
    };

    // Crear el debounce una sola vez y mantenerlo estable
    const debouncedGetCities = useRef(debounce(getCities, 300)).current;

    /*=========================================================
    SELECT CITY FUNCTION
    =========================================================*/
    const selectCity = useCallback((city: any) => {
        const city_id = city.city_id;
        const city_name = city.city_name;
        const selector = `[${attributeName}="${pc_city}"]`;
        const element = document.querySelector(selector) as HTMLInputElement;
        if (element) {
            element.value = city_name || '';
            const event = new Event('input', { bubbles: true });
            element.dispatchEvent(event);
        }
        setFormValues((prevFormValues: any) => {
            const updates: any = {
                [pc_city.replace('-', '_')]: city_name,
                [pc_city.replace('-', '_') + '_id']: city_id
            };
            // Sincronizar si sameShippingBilling está activo
            if (prevFormValues.sameShippingBilling) {
                if (pc_city === 'city') {
                    updates['s_city'] = city_name;
                    updates['s_city_id'] = city_id;
                } else if (pc_city === 's-city') {
                    updates['city'] = city_name;
                    updates['city_id'] = city_id;
                }
            }
            return {
                ...prevFormValues,
                ...updates
            };
        });
        setPreviousValue(city_name); // Actualizar el valor anterior
    }, [attributeName, pc_city, setFormValues, setPreviousValue]);

    /*=========================================================
    USE EFFECT TO ATTACH EVENT LISTENERS
    =========================================================*/
    useEffect(() => {
        const selector = `[${attributeName}="${pc_city}"]`;
        const element = document.querySelector(selector) as HTMLInputElement;
        if (element) {
            element.setAttribute('autocomplete', 'new-password');
            const updatePosition = () => {
                const rect = element.getBoundingClientRect();
                // Busca el contenedor del popup
                const popupInner = element.closest('.sub1_wrap');
                let offsetTop = rect.bottom + window.scrollY;
                let offsetLeft = rect.left + window.scrollX;
                if (popupInner) {
                    const popupRect = popupInner.getBoundingClientRect();
                    offsetTop = rect.bottom - popupRect.top;
                    offsetLeft = rect.left - popupRect.left;
                }
                setInputPosition({ 
                    top: offsetTop, 
                    left: offsetLeft, 
                    width: rect.width 
                });
            };

            const handleInput = (e: any) => {
                const value = e.target.value;
                console.log('handleInput called with value:', value); // Debug log
                debouncedGetCities(value);
            };

            const handleFocus = () => {
                const element = document.querySelector(selector) as HTMLInputElement;
                if (element) {
                    setPreviousValue(element.value); // Guardar el valor anterior al hacer focus
                }
                updatePosition();
                setIsVisible(true);
                debouncedGetCities(''); // Trigger search with empty keyword on focus
            };

            const handleBlur = () => {
                setTimeout(() => {
                    // Si se está seleccionando una opción, no hacer nada
                    if (isSelectingRef.current) {
                        setIsSelecting(false);
                        return;
                    }
                    
                    const element = document.querySelector(selector) as HTMLInputElement;
                    if (element) {
                        const currentValue = element.value.trim();
                        
                        if (currentValue === '') {
                            // Si el input está vacío, dejarlo vacío
                            setFormValues((prevFormValues: any) => {
                                const updates: any = {
                                    [pc_city.replace('-', '_')]: '',
                                    [pc_city.replace('-', '_') + '_id']: ''
                                };
                                // Sincronizar si sameShippingBilling está activo
                                if (prevFormValues.sameShippingBilling) {
                                    if (pc_city === 'city') {
                                        updates['s_city'] = '';
                                        updates['s_city_id'] = '';
                                    } else if (pc_city === 's-city') {
                                        updates['city'] = '';
                                        updates['city_id'] = '';
                                    }
                                }
                                return {
                                    ...prevFormValues,
                                    ...updates
                                };
                            });
                        } else if (citiesRef.current.length > 0) {
                            // Si hay ciudades disponibles, seleccionar la primera
                            const firstCity = citiesRef.current[0];
                            selectCity(firstCity);
                        } else if (currentValue !== previousValueRef.current) {
                            // Si no hay resultados y el valor cambió, volver al valor anterior
                            element.value = previousValueRef.current;
                            const event = new Event('input', { bubbles: true });
                            element.dispatchEvent(event);
                            
                            // Restaurar el estado del formulario al valor anterior
                            const previousCityId = formValues[pc_city.replace('-', '_') + '_id'] || '';
                            setFormValues((prevFormValues: any) => {
                                const updates: any = {
                                    [pc_city.replace('-', '_')]: previousValueRef.current,
                                    [pc_city.replace('-', '_') + '_id']: previousCityId
                                };
                                // Sincronizar si sameShippingBilling está activo
                                if (prevFormValues.sameShippingBilling) {
                                    if (pc_city === 'city') {
                                        updates['s_city'] = previousValueRef.current;
                                        updates['s_city_id'] = previousCityId;
                                    } else if (pc_city === 's-city') {
                                        updates['city'] = previousValueRef.current;
                                        updates['city_id'] = previousCityId;
                                    }
                                }
                                return {
                                    ...prevFormValues,
                                    ...updates
                                };
                            });
                        }
                    }
                    setIsVisible(false);
                }, 200); // Retraso para permitir clic en la sugerencia
            };

            element.addEventListener("input", handleInput);
            element.addEventListener("focus", handleFocus);
            element.addEventListener("blur", handleBlur);

            // Actualizar posición en cambio de tamaño de ventana
            window.addEventListener('resize', updatePosition);

            return () => {
                element.removeEventListener("input", handleInput);
                element.removeEventListener("focus", handleFocus);
                element.removeEventListener("blur", handleBlur);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [attributeName, pc_city, selectCity]);

    /*=========================================================
    RENDER
    =========================================================*/
    return (
        isVisible ? (
            <div
                className='cities_list'
                style={{ 
                    position: 'absolute', 
                    top: `${inputPosition.top}px`, 
                    left: `${inputPosition.left}px`, 
                    width: `${inputPosition.width}px`, 
                    backgroundColor: 'white', 
                    border: '1px solid #ccc', 
                    zIndex: 1000 
                }}
            >
                {cities.map((city: any) => (
                    <div
                        key={city.city_id}
                        className='autocomplete_item'
                        data-city-id={city.city_id}
                        data-city-name={city.city_name}
                        onMouseDown={() => {
                            setIsSelecting(true);
                            selectCity(city);
                            setIsVisible(false);
                        }}
                    >
                        {city.city_name}
                    </div>
                ))}
            </div>
        ) : null
    );
}
